
import {defineComponent} from "vue";
import {useToast} from "vue-toastification";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import BaseOffcanvasButtonClose from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonClose.vue";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import VisibilityRule from "@/components/layouts/VisibilityRule.vue";
import {ContentVisibilityRuleRestDtoModel} from "@/models/api/pharma-cms-menu/ContentVisibilityRuleRestDtoModel";
import {PopupVisibilityRuleRestService} from "@/services/rest/cms-popup/PopupVisibilityRuleRestService";

export default defineComponent({
  name: "VisibilityRuleWrapper",
  emits: ['edit'],
  components: {
    VisibilityRule,
    BaseOffcanvasButtonClose,
    AlertError2,
  },
  props: {
    popupId: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: false,
    },
    mode: {
      type: String,
      required: true,
    },
    visibilityRuleOptionsKeys: {
      type: Array,
      required: false
    }
  },
  data() {
    return {
      visibilityRule: ContentVisibilityRuleRestDtoModel.createWithDefaults(),
      visibilityRuleChanged: false,
      toaster: useToast(),
      pageUI: UIStateDto.createWithDefaults(),
    }
  },
  mounted() {
    this.reloadContent();
  },
  computed: {
    PageDetailModeEnum() {
      return PageDetailModeEnum
    },
  },
  methods: {
    async submitData() {
      this.pageUI
        .clearError()
        .setNotReady();

      try {
        if (this.mode === PageDetailModeEnum.NEW) {
          await PopupVisibilityRuleRestService.getInstance().addPopupVisibilityRule(this.visibilityRule, this.popupId);
          this.$emit('edit');
          this.closeCurrentOffCanvas();
          return;
        }

        await PopupVisibilityRuleRestService.getInstance().updatePopupVisibilityRule(this.id, this.visibilityRule);
        this.$emit('edit');
        await this.afterSave();
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },
    async reloadContent() {
      this.pageUI
        .clearError()
        .setNotReady();
      try {
        if (this.mode === PageDetailModeEnum.EDIT) {
          this.visibilityRule = await PopupVisibilityRuleRestService.getInstance().getPopupVisibilityRule(this.id);
        }
        this.visibilityRuleChanged = false;
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },
    async afterSave() {
      this.toaster.success(this.$t('savedSuccessfully'));
      await this.reloadContent();
    },
    onVisibilityRuleChanged(payload: ContentVisibilityRuleRestDtoModel): void {
      this.visibilityRule = payload;
      this.visibilityRuleChanged = true;
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
        .create()
        .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .catch(exceptions, true);
    },
    closeCurrentOffCanvas(): void {
      // Wait one tick to ensure DOM is updated after the form is submitted
      this.$nextTick(() => {
        const closeButton = this.$refs.closeButton as any;
        if (closeButton && closeButton.$el) {
          closeButton.$el.click();
        }
      });
    }
  },
});
